import {default as classnames} from "classnames";
import snakeCase from "lodash/snakeCase";
import {HexColorInput} from "react-colorful";
import {Controller, useFormContext} from "react-hook-form";

import {ColorPicker} from "@/components/colorPicker/ColorPicker.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Input} from "@/components/input/Input.tsx";
import {getFormError} from "@/lib/form/form.utils.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

interface ColorPickerFieldProps extends FieldProps {
  title?: string;
  hasTextInput?: boolean;
  label?: string;
}

export const ColorPickerField = ({
  name,
  title,
  hasTextInput = true,
  className,
  label,
  id,
  tooltip,
  helpText,
  supportLink,
  required,
  disabled,
}: ColorPickerFieldProps) => {
  const {
    control,
    setFocus,
    formState: {errors},
  } = useFormContext();
  const fieldError = getFormError(name, errors);
  const elId = snakeCase(name);
  return (
    <div className={className}>
      {label && (
        <FormLabel helpText={tooltip} forId={id || name} required={required}>
          {label}
        </FormLabel>
      )}
      <div className={classnames("d-flex", className)} id={elId}>
        <div className={"me-2"}>
          <Controller
            name={name}
            control={control}
            render={({field: {value, onChange}}) => {
              return (
                <ColorPicker
                  color_hex={value}
                  onChange={(color_hex) => {
                    onChange(color_hex);
                  }}
                  hide={() => {
                    setFocus(name);
                  }}
                  title={title ? title : label}
                  disabled={disabled}
                />
              );
            }}
          />
        </div>
        <>
          {hasTextInput && (
            <Controller
              name={name}
              control={control}
              render={({field: {value, onChange, onBlur}}) => (
                <Input
                  as={HexColorInput}
                  color={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  prefixed
                  id={name || id}
                  aria-invalid={!!fieldError}
                  disabled={disabled}
                />
              )}
            />
          )}
        </>
      </div>
      <FormSubtext subText={helpText} supportLink={supportLink} error={fieldError?.toString()} />
    </div>
  );
};
